import type { MonthlyBucket, RestaurantTracker } from '../../../../services/admin/clientAnalyticsService';

/** Which side of the business the cost% lens looks at. */
export type Lens = 'total' | 'food' | 'drinks';

export interface QuarterDef {
  key: string; // e.g. "2025-Q4"
  label: string; // e.g. "Q4 '25"
  months: string[]; // YYYY-MM keys belonging to this quarter (only those present in range)
}

/** Euro pair for a single lens. */
export interface EuroPair {
  omzet: number;
  inslag: number;
}

const ZERO: EuroPair = { omzet: 0, inslag: 0 };

/** Build the ordered list of quarters covering the given YYYY-MM months. */
export function quartersForMonths(months: string[]): QuarterDef[] {
  const byQuarter = new Map<string, QuarterDef>();
  for (const m of months) {
    const [yearStr, monthStr] = m.split('-');
    const year = Number(yearStr);
    const q = Math.floor((Number(monthStr) - 1) / 3) + 1;
    const key = `${year}-Q${q}`;
    let def = byQuarter.get(key);
    if (!def) {
      def = { key, label: `Q${q} '${String(year).slice(2)}`, months: [] };
      byQuarter.set(key, def);
    }
    def.months.push(m);
  }
  return Array.from(byQuarter.values()).sort((a, b) => a.key.localeCompare(b.key));
}

export type Granularity = 'months' | 'quarters';

/** A matrix column — either a single month or a quarter subtotal. */
export interface MatrixColumn {
  key: string;
  label: string;
  type: 'month' | 'quarter';
  months: string[]; // month keys to sum for this column
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "2025-10" → "Oct '25". */
export function formatMonthLabel(month: string): string {
  const [year, m] = month.split('-').map(Number);
  return `${MONTH_NAMES[m - 1]} '${String(year).slice(2)}`;
}

/**
 * Ordered matrix columns. At 'months' granularity each quarter's months are
 * listed followed by the quarter subtotal (matching the Excel Inslag Matrix).
 */
export function buildColumns(months: string[], granularity: Granularity): MatrixColumn[] {
  const quarters = quartersForMonths(months);
  if (granularity === 'quarters') {
    return quarters.map((q) => ({ key: q.key, label: q.label, type: 'quarter', months: q.months }));
  }
  const cols: MatrixColumn[] = [];
  for (const q of quarters) {
    for (const m of q.months) {
      cols.push({ key: m, label: formatMonthLabel(m), type: 'month', months: [m] });
    }
    cols.push({ key: q.key, label: q.label, type: 'quarter', months: q.months });
  }
  return cols;
}

/** Omzet/inslag for one bucket under a lens. */
export function lensEuros(b: MonthlyBucket, lens: Lens): EuroPair {
  if (lens === 'food') return { omzet: b.omzetFood, inslag: b.inslagFood };
  if (lens === 'drinks') return { omzet: b.omzetDrinks, inslag: b.inslagDrinks };
  return { omzet: b.omzetTotal, inslag: b.inslagTotal };
}

function addPairs(a: EuroPair, b: EuroPair): EuroPair {
  return { omzet: a.omzet + b.omzet, inslag: a.inslag + b.inslag };
}

/** Sum euros across a subset of months of one restaurant, for a lens. */
export function sumMonths(buckets: MonthlyBucket[], monthKeys: string[], lens: Lens): EuroPair {
  const set = new Set(monthKeys);
  return buckets.reduce(
    (acc, b) => (set.has(b.month) ? addPairs(acc, lensEuros(b, lens)) : acc),
    ZERO
  );
}

/** Sum euros across several restaurants for a subset of months (client / grand-total rollups). */
export function sumRestaurants(
  restaurants: RestaurantTracker[],
  monthKeys: string[],
  lens: Lens
): EuroPair {
  return restaurants.reduce((acc, r) => addPairs(acc, sumMonths(r.months, monthKeys, lens)), ZERO);
}

/** Cost% ratio (inslag / omzet), or null when there is no omzet. */
export function costPctRatio(pair: EuroPair): number | null {
  return pair.omzet > 0 ? pair.inslag / pair.omzet : null;
}

/** Format a 0..1 ratio as a percentage string, or an em-dash when null. */
export function formatPct(ratio: number | null, digits = 1): string {
  if (ratio === null || !Number.isFinite(ratio)) return '—';
  return `${(ratio * 100).toFixed(digits)}%`;
}

/** Format a percentage-point delta with sign, or em-dash. */
export function formatPp(pp: number | null, digits = 1): string {
  if (pp === null || !Number.isFinite(pp)) return '—';
  const sign = pp > 0 ? '+' : '';
  return `${sign}${pp.toFixed(digits)} pp`;
}

/** Compact euro formatting (e.g. €12.3k). */
export function formatEuro(value: number): string {
  if (!Number.isFinite(value)) return '—';
  if (Math.abs(value) >= 1000) return `€${(value / 1000).toFixed(1)}k`;
  return `€${value.toFixed(0)}`;
}

/** Tailwind text colour for a trend label. */
export function trendColor(trend: string | null): string {
  if (!trend) return 'text-gray-400 dark:text-gray-500';
  if (trend.includes('improvement')) return 'text-green-600 dark:text-green-400';
  if (trend === 'Slight increase') return 'text-amber-600 dark:text-amber-400';
  return 'text-red-600 dark:text-red-400';
}
